import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Title from "../common/Title";
import EnhancedGrid from "../common/EnhancedGrid";
import { FaPerson } from "react-icons/fa6";
import { FaUsers } from "react-icons/fa";
import Utils from "../../utils";
import { userActions } from "../../actions/user.actions";
import UsersGridFilter from "./UsersGridFilter";
import { MdPersonOff, MdEdit } from "react-icons/md";
import ConfirmModal from "../common/ConfirmModal";
import { Badge, Alert, Button } from "react-bootstrap";
import moment from "moment";
import GridHelper from "../helpers/gridHelper";
import { usePermissions } from "../../hooks/usePermissions";

function UsersGrid(props) {
  const { hasPermission } = usePermissions(props.user);
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userReducer = useSelector((state) => state.user);
  const [confirmDeleteModalShow, setConfirmDeleteModalShow] = useState(false);
  const [deleteUserId, setDeleteUserId] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  // The confirm modal closes the moment "Yes" is clicked, before the delete request even starts,
  // so a failed delete previously had nowhere to surface — the modal just vanished with no
  // indication anything went wrong. Track it here and show it once userReducer.removeFailed flips.
  const [showDeleteError, setShowDeleteError] = useState(false);

  useEffect(() => {
    if (userReducer.removeFailed) {
      setShowDeleteError(true);
    }
  }, [userReducer.removeFailed]);

  const filterInitialState = {
    firstName: "",
    lastName: "",
    email: "",
    lastAuthenticatedWith: "",
    isAdmin: null,
    isSuperUser: null,
  };
  const [filter, setFilter] = useState(filterInitialState);

  useEffect(() => {
    let queryParams = Utils.getQueryStringParams(location);

    // Filter by client based on what's selected in the client dropdown
    // - If a specific client is selected (clientId is set), filter by that client
    // - If "All Clients" is selected (clientId is empty), show all users (Super Users only)
    if (props.user.clientId && props.user.clientGuid) {
      queryParams["clientId"] = props.user.clientId;
      queryParams["clientGuid"] = props.user.clientGuid;
    }

    dispatch(userActions.get(queryParams));

    // Initialize filter from URL
    setFilter({
      firstName: queryParams["firstName"],
      lastName: queryParams["lastName"],
      email: queryParams["email"],
      lastAuthenticatedWith: queryParams["lastAuthenticatedWith"],
      isAdmin: queryParams["isAdmin"],
      isSuperUser: queryParams["isSuperUser"],
    });

    // eslint-disable-next-line
  }, [location, props.refreshSignal, userReducer.removeSuccess]);

  const handleDeleteUser = () => {
    setConfirmDeleteModalShow(false);
    setShowDeleteError(false);
    let requestParameters = {
      userId: selectedUser.userId,
      // Use the selected user's client info, or fall back to current user's client info
      clientId: selectedUser.clientId || props.user.clientId,
      clientGuid: selectedUser.clientGuid || props.user.clientGuid,
    };

    dispatch(userActions.remove(requestParameters));
    setSelectedUser(null);
  };

  // Check if user is admin, super user, or product team
  const canManageUsers = props.user.isAdmin || props.user.isSuperUser || props.user.isProductTeam;

  if (!canManageUsers) {
    return (
      <>
        {!props.hideTitle && <Title text="Users" icon={<FaPerson className="mb-1" />} />}
        <Alert variant="danger">
          User management is only available to Admins, Super Users, and Product Team members.
        </Alert>
      </>
    );
  }

  let displayColumns = [
    "firstName",
    "lastName",
    "email",
    "isAdmin",
    "isSuperUser",
    "isActive",
    "createdOn",
    "lastAuthenticatedWith",
    "lastSignInDateOn",
  ];
  if (props.user.isSuperUser) displayColumns.push("clientName");

  const data = userReducer.paginatedResult?.data.map((u) => {
    return {
      ...u,
      createdOn: Utils.convertToDateOnly(u.createdOn),
      lastAuthenticatedWith: u.lastAuthenticatedWith ?? "CloudSync",
      lastSignInDateOn: u.lastSignInDateOn
        ? moment(Utils.convertToDateTime(u.lastSignInDateOn)).fromNow()
        : "Never",
      isAdmin: u.isAdmin ? <Badge bg="primary">Admin</Badge> : "",
      isSuperUser: u.isSuperUser ? <Badge bg="primary">Super User</Badge> : "",
      // Backend uses 'active' not 'isActive'
      isActive: (u.active !== undefined ? u.active : u.isActive) ? <Badge bg="success">Active</Badge> : <Badge bg="secondary">Inactive</Badge>,
    };
  });

  return (
    <>
      {!props.hideTitle && <Title text="Users" icon={<FaPerson className="mb-1" />} />}

      {showDeleteError && (
        <Alert variant="danger" dismissible onClose={() => setShowDeleteError(false)}>
          Failed to deactivate the user. Please try again, or contact support if the issue persists.
        </Alert>
      )}

      {/* Action Buttons Bar */}
      <div className="d-flex gap-2 mb-3 p-3 bg-light rounded"
           style={{
             opacity: selectedUser ? 1 : 0.6,
             transition: 'opacity 0.3s ease'
           }}>

        <Button
          variant="outline-primary"
          size="sm"
          disabled={!selectedUser}
          onClick={() => {
            if (selectedUser) {
              props.onEditUser ? props.onEditUser(selectedUser.userId) : navigate(`edit?userId=${selectedUser.userId}`);
            }
          }}
        >
          <MdEdit className="mb-1 me-2" />
          Edit
        </Button>

        <Button
          variant="outline-danger"
          size="sm"
          disabled={!selectedUser}
          onClick={() => {
            if (selectedUser) {
              setConfirmDeleteModalShow(true);
            }
          }}
        >
          <MdPersonOff className="mb-1 me-2" />
          Deactivate
        </Button>

        {selectedUser && (
          <div className="ms-auto align-self-center">
            <small className="text-muted">
              Selected: <strong>{selectedUser.firstName} {selectedUser.lastName}</strong> ({selectedUser.email})
            </small>
          </div>
        )}
      </div>

      <UsersGridFilter filter={filter} />

      <EnhancedGrid
        gridId="user"
        fillHeight={props.hideTitle}
        data={data}
        displayColumns={displayColumns}
        emptyState={{
          icon: <FaUsers size={80} className="text-muted mb-3" />,
          message: "No users found",
          subtitle: "Invite users to give them access to the portal.",
        }}
        idColumn={"userId"}
        selectedRow={selectedUser}
        onRowClick={(user) => setSelectedUser(user)}
        pageNumber={(userReducer.paginatedResult?.pageNumber ?? 1) - 1}
        totalPages={userReducer.paginatedResult?.totalPages ?? 0}
        loading={userReducer.loading}
        onPageChange={GridHelper.handlePageClick}
        onSort={GridHelper.handleSortClick}
      />

      <ConfirmModal
        show={confirmDeleteModalShow}
        title="Deactivate User"
        question={`Are you sure you want to deactivate ${selectedUser?.firstName} ${selectedUser?.lastName} (${selectedUser?.email})? They will remain visible here but will no longer be able to sign in.`}
        noButtonText="No"
        yesButtonText="Yes, deactivate"
        handleClose={() => {
          setConfirmDeleteModalShow(false);
        }}
        handleConfirm={() => {
          handleDeleteUser();
        }}
      />
    </>
  );
}

export default UsersGrid;
